import type { EmployeeAlerts } from '@cpce/shared';
import { Building, IdCard, User } from 'lucide-react';
import { useState } from 'react';
import { useEmployee, useEmployeesInfinite } from '@/api/employees/employees.queries';
import type { EmployeeWithBeneficiariesFormatted } from '@/api/employees/employees.types';
import { isNotFoundError } from '@/api/queries.utils';
import { NotFoundState } from '@/components/NotFoundState';
import { PageSpinner } from '@/components/PageSpinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/Tabs';
import { Route } from '@/routes/$commiteeSlug/beneficiaries/employees/{-$id}';
import { useCommiteeSlugNavigate } from '@/utils/router';
import { BeneficiaryPageBase } from '../../components/BeneficiaryPageBase';
import { EmployeeHistory } from '../history/components/EmployeeHistory';
import { EmployeeOrders } from '../orders/components/EmployeeOrders';
import { EmployeeBeneficiaries } from './EmployeeBeneficiaries';
import { EmployeeFinancialExchanges } from './EmployeeFinancialExchanges';
import { EmployeeInformation } from './EmployeeInformation';

interface EmployeePageProps {
  id: number;
}

export enum EmployeeTabs {
  Information = 'Informations',
  SubBeneficiaries = 'Ayants-droit',
  History = 'Historiques',
  Orders = 'Commandes billetterie',
  FinancialExchanges = 'Échanges financiers',
}

const EMPLOYEE_TAB_CONTENTS: Record<
  EmployeeTabs,
  React.ComponentType<{ employee: EmployeeWithBeneficiariesFormatted }>
> = {
  [EmployeeTabs.Information]: EmployeeInformation,
  [EmployeeTabs.SubBeneficiaries]: EmployeeBeneficiaries,
  [EmployeeTabs.History]: EmployeeHistory,
  [EmployeeTabs.Orders]: EmployeeOrders,
  [EmployeeTabs.FinancialExchanges]: EmployeeFinancialExchanges,
};

export function EmployeePage(props: EmployeePageProps) {
  const { id } = props;
  const { tab = EmployeeTabs.Information } = Route.useSearch();
  const routeNavigate = Route.useNavigate();
  const navigate = useCommiteeSlugNavigate();
  const { data: employee, isLoading, error } = useEmployee(id);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const { data: employees, isLoading: isLoadingEmployees } = useEmployeesInfinite({
    search: employeeSearch,
  });

  if (isLoading) return <PageSpinner />;
  if (!employee)
    return isNotFoundError(error) ? (
      <NotFoundState
        title="Salarié introuvable"
        backTo="/$commiteeSlug/beneficiaries/employees/{-$id}"
        backLabel="Retour aux salariés"
      />
    ) : null;

  return (
    <Tabs
      value={tab}
      onValueChange={(value) =>
        routeNavigate({
          search: (prev) => ({ ...prev, tab: value as EmployeeTabs }),
          replace: true,
        })
      }
    >
      <BeneficiaryPageBase
        pathLink={[
          {
            label: 'Salariés',
            navigate: {
              to: '/$commiteeSlug/beneficiaries/employees/{-$id}',
              params: { id: undefined },
            },
          },
          {
            label:
              `${employee.beneficiary.firstName || ''} ${employee.beneficiary.lastName || ''}`.trim(),
          },
        ]}
        searchBar={{
          placeholder: 'Rechercher un autre salarié',
          options: employees.map((employee) => ({
            id: employee.id,
            label: employee.beneficiary.fullName ?? '-',
            value: employee,
          })),
          onSelect: (employee) => {
            navigate({
              to: '/$commiteeSlug/beneficiaries/employees/{-$id}',
              params: { id: employee.id.toString() },
            });
          },
          onSearchChange: setEmployeeSearch,
          isLoading: isLoadingEmployees,
        }}
        beneficiary={{
          data: employee.beneficiary,
          infoBlocks: [
            { id: 'service', icon: <Building />, text: employee.service ?? undefined },
            { id: 'status', icon: <User />, text: employee.statusFormatted },
            { id: 'matricule', icon: <IdCard />, text: employee.matricule ?? undefined },
          ],
        }}
        subHeader={
          <TabsList>
            {Object.values(EmployeeTabs).map((tab) => {
              const alertCount = getTabAlertCount(tab, employee.alerts);
              return (
                <TabsTrigger key={tab} value={tab}>
                  {tab}
                  {alertCount > 0 && (
                    <div className="flex size-4 rounded-full bg-red-600 items-center justify-center">
                      <div className="text-white text-xs">{alertCount} </div>
                    </div>
                  )}
                </TabsTrigger>
              );
            })}
          </TabsList>
        }
      >
        {Object.entries(EMPLOYEE_TAB_CONTENTS).map(([tab, TabContent]) => (
          <TabsContent key={tab} value={tab}>
            <TabContent employee={employee} />
          </TabsContent>
        ))}
      </BeneficiaryPageBase>
    </Tabs>
  );
}

function getTabAlertCount(tab: EmployeeTabs, alerts: EmployeeAlerts): number {
  switch (tab) {
    case EmployeeTabs.Information:
      return alerts.documentValidationRequired;
    case EmployeeTabs.SubBeneficiaries:
      return alerts.beneficiaryDocumentValidationRequired;
    case EmployeeTabs.FinancialExchanges:
      return alerts.debt ? 1 : 0;
    case EmployeeTabs.History:
      return 0;
    case EmployeeTabs.Orders:
      return 0;
  }
}
